'use client';

import React from 'react';
import { Text, Flex, Box } from '@skin-seoul/design-system';
import { CLASSNAME } from '@skin-seoul/service';
import { useSafeRouter } from '@/hooks/useSafeRouter';
import { useSearchParams } from 'next/navigation';
import { type TaxonomyCategory, generateCategoryUrl, findTermIdBySlug, findCategoryByTermId, findParentSubcategoryForSubSubcategory, useDynamicCategories } from '../utils/categoryUtils';
interface SubSubcategoriesSectionProps {
  subcategory?: string;
  subSubcategory?: string;
}
const SubSubcategoriesSection = ({
  subcategory,
  subSubcategory
}: SubSubcategoriesSectionProps) => {
  const router = useSafeRouter();
  const searchParams = useSearchParams();
  const {
    categories: mainCategories
  } = useDynamicCategories();
  const parentSubcategory = React.useMemo(() => {
    if (subSubcategory) {
      const subSubcategoryTermId = findTermIdBySlug(mainCategories, subSubcategory);
      if (!subSubcategoryTermId) {
        return null;
      }
      return findParentSubcategoryForSubSubcategory(mainCategories, subSubcategoryTermId);
    }
    if (subcategory) {
      const subcategoryTermId = findTermIdBySlug(mainCategories, subcategory);
      if (!subcategoryTermId) {
        return null;
      }
      return findCategoryByTermId(mainCategories, subcategoryTermId);
    }
    return null;
  }, [subcategory, subSubcategory, mainCategories]);
  const subSubcategories = parentSubcategory?.subCategories || [];
  if (!parentSubcategory || subSubcategories.length === 0) {
    return null;
  }
  const handleSubSubcategoryClick = (subSubcategoryItem: TaxonomyCategory) => {
    const categoryUrl = generateCategoryUrl(mainCategories, subSubcategoryItem.termId);
    if (categoryUrl !== '/collections/') {
      const filtersParam = searchParams.get('filters');
      const targetUrl = filtersParam ? `${categoryUrl}?filters=${filtersParam}` : categoryUrl;
      router.push(targetUrl);
    }
  };
  return <Box borderBottom={{
    base: 'none',
    tablet: '1px solid'
  }} borderColor={{
    base: 'transparent',
    tablet: 'neutral.100'
  }} marginBottom={{
    base: 4,
    tablet: 0
  }} data-sentry-element="Box" data-sentry-component="SubSubcategoriesSection" data-sentry-source-file="SubSubcategoriesSection.tsx">
      <Box paddingY={{
      base: 0,
      tablet: 6
    }} data-sentry-element="Box" data-sentry-source-file="SubSubcategoriesSection.tsx">
        <Text fontWeight="regular" textStyle={{
        base: 'paragraph.xsmall',
        tablet: 'label.large'
      }} color="neutral.600" marginBottom={{
        base: 3,
        tablet: 0
      }} data-sentry-element="Text" data-sentry-source-file="SubSubcategoriesSection.tsx">
          Showing
        </Text>
      </Box>
      <Box paddingBottom={4} data-sentry-element="Box" data-sentry-source-file="SubSubcategoriesSection.tsx">
        <Flex flexWrap="wrap" gap={2} data-sentry-element="Flex" data-sentry-source-file="SubSubcategoriesSection.tsx">
          {subSubcategories.map((subSubcategoryItem: TaxonomyCategory) => {
          const isActive = subSubcategory === subSubcategoryItem.slug;
          const borderColor = isActive ? 'primary.400' : 'neutral.100';
          const backgroundColor = {
            base: isActive ? 'primary.50' : 'transparent'
          };
          const textColor = isActive ? 'primary.400' : 'neutral.600';
          return <Box key={subSubcategoryItem.termId} borderRadius="8px" border="1px solid" borderColor={borderColor} backgroundColor={backgroundColor} paddingY={2} paddingX={3} cursor="pointer" onClick={() => handleSubSubcategoryClick(subSubcategoryItem)} _hover={{
            borderColor: 'primary.400',
            backgroundColor: {
              base: 'primary.50',
              tablet: 'transparent'
            }
          }}>
                <Text fontWeight="regular" textStyle="paragraph.small" color={textColor} _hover={{
              color: 'primary.400'
            }} className={CLASSNAME.NOT_TRANSLATE}>
                  {subSubcategoryItem.name}
                </Text>
              </Box>;
        })}
        </Flex>
      </Box>
    </Box>;
};
export default SubSubcategoriesSection;